import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { getTurnstileSiteKey, isCaptchaEnabled } from '../../utils/captcha'

export type CaptchaHandle = {
  /** Kasuje token i każe Cloudflare wystawić nowy. */
  reset: () => void
}

interface CaptchaGateProps {
  /** Token po rozwiązaniu wyzwania, null gdy wygasł albo widget się nie wczytał. */
  onToken: (token: string | null) => void
  /** Nazwa formularza w statystykach Cloudflare ([a-zA-Z0-9_-], do 32 znaków). */
  action?: string
  className?: string
}

/**
 * Widget Turnstile dla formularzy auth. Token jest jednorazowy i wygasa po
 * ~5 minutach, więc po każdej nieudanej próbie wysyłki trzeba wywołać reset().
 * Bez VITE_TURNSTILE_SITE_KEY komponent nie renderuje nic.
 */
export const CaptchaGate = forwardRef<CaptchaHandle, CaptchaGateProps>(
  function CaptchaGate({ onToken, action, className }, ref) {
    const widgetRef = useRef<TurnstileInstance | null>(null)
    const [loadFailed, setLoadFailed] = useState(false)

    useImperativeHandle(ref, () => ({
      reset: () => {
        setLoadFailed(false)
        onToken(null)
        widgetRef.current?.reset()
      }
    }), [onToken])

    if (!isCaptchaEnabled()) {
      return null
    }

    return (
      <div className={className}>
        <Turnstile
          ref={widgetRef}
          siteKey={getTurnstileSiteKey()}
          options={{ action, theme: 'light', language: 'pl' }}
          onSuccess={(token) => {
            setLoadFailed(false)
            onToken(token)
          }}
          onExpire={() => onToken(null)}
          onError={() => {
            setLoadFailed(true)
            onToken(null)
          }}
        />
        {loadFailed && (
          <p className="mt-2 text-xs text-red-600">
            Nie udało się wczytać weryfikacji antybotowej. Wyłącz blokadę reklam dla 2domy.pl i odśwież stronę.
          </p>
        )}
      </div>
    )
  }
)
